import { useState, useMemo } from "react";

const DEFAULT_PAGE_SIZES = [5, 10, 20, "All"];

const AddressTable = ({ addresses }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [filterType, setFilterType] = useState("All");

  // Get unique address types for filtering dropdown
  const uniqueTypes = useMemo(() => {
    const allTypes = addresses.flatMap(addr =>
      Array.isArray(addr.type) ? addr.type : [addr.type]
    );
    return ["All", ...Array.from(new Set(allTypes.filter(Boolean)))];
  }, [addresses]);

  // Filter and sort addresses
  const filteredSortedAddresses = useMemo(() => {
    let filtered = addresses;
    if (filterType !== "All") {
      filtered = addresses.filter(addr =>
        Array.isArray(addr.type)
          ? addr.type.includes(filterType)
          : addr.type === filterType
      );
    }

    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortConfig.key] || "";
        const bVal = b[sortConfig.key] || "";
        const aStr = Array.isArray(aVal) ? aVal.join(", ") : aVal;
        const bStr = Array.isArray(bVal) ? bVal.join(", ") : bVal;
        return sortConfig.direction === "asc"
          ? aStr.localeCompare(bStr)
          : bStr.localeCompare(aStr);
      });
    }
    return filtered;
  }, [addresses, filterType, sortConfig]);

  // Calculate pagination details
  const actualPageSize = pageSize === "All" ? filteredSortedAddresses.length : pageSize;
  const totalPages = Math.ceil(filteredSortedAddresses.length / actualPageSize);
  const currentPageAdjusted = currentPage > totalPages ? totalPages || 1 : currentPage;

  const paginatedAddresses = filteredSortedAddresses.slice(
    (currentPageAdjusted - 1) * actualPageSize,
    currentPageAdjusted * actualPageSize
  );

  // Handlers
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const handlePageSizeChange = (e) => {
    setPageSize(e.target.value === "All" ? "All" : Number(e.target.value));
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    setFilterType(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="addresses-section">
      <div>
        <label className="filterLabel" htmlFor="filterType">Filter by Address Type :</label>
        <select className="identifierFilters" id="filterType" value={filterType} onChange={handleFilterChange} style={{ padding: "5px" }}>
          {uniqueTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      <table className="address-table">
        <thead>
          <tr>
            {[
              "sources",
              "type",
              "line1",
              "line2",
              "city",
              "state",
              "post_code",
              "country"
            ].map((key, idx) => (
              <th key={idx} onClick={() => handleSort(key)} style={{ cursor: "pointer" }}>
                {formatHeader(key)}
                <span style={{ fontSize:12 }}>{sortConfig.key === key ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : " ▼"}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginatedAddresses.length > 0 ? (
            paginatedAddresses.map((addr, idx) => (
              <tr key={idx}>
                <td>{Array.isArray(addr.sources) ? addr.sources.join(", ") || "-" : addr.sources || "-"}</td>
                <td>{Array.isArray(addr.type) ? addr.type.join(", ") || "-" : addr.type || "-"}</td>
                <td>{addr.line1 || "-"}</td>
                <td>{addr.line2 || "-"}</td>
                <td>{addr.city || "-"}</td>
                <td>{addr.state || "-"}</td>
                <td>{addr.post_code || "-"}</td>
                <td>{addr.country || "-"}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={8} style={{ textAlign: "center", padding: "1rem" }}>
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination controls */}
      {totalPages !== 0 && <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>

        <label htmlFor="pageSize">Rows per page : </label>
        <select id="pageSize" value={pageSize} onChange={handlePageSizeChange} style={{ padding: "5px" }}>
          {DEFAULT_PAGE_SIZES.map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>

        <button
          className={currentPageAdjusted === 1 ? "paginatedTableBtnDisabled" : "paginatedTableBtn"}
          disabled={currentPageAdjusted === 1}
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
        >
          Previous
        </button>

        <span>
          Page {currentPageAdjusted} of {totalPages}
        </span>

        <button
          className={currentPageAdjusted === totalPages ? "paginatedTableBtnDisabled" : "paginatedTableBtn"}
          disabled={currentPageAdjusted === totalPages}
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
        >
          Next
        </button>
      </div>}
    </div>
  );
};


const formatHeader = (key) => {
  return {
    sources: "Address Source",
    type: "Address Type",
    line1: "Address Line 1",
    line2: "Address Line 2",
    city: "City",
    state: "State",
    post_code: "Postal Code",
    country: "Country"
  }[key];
};

export default AddressTable;
